    import React, { useState, useEffect } from 'react';
    import { useTheme } from '../../context/ThemeContext';
    import { useAuth } from '../../context/AuthContext';
    import { fetchNodes, fetchAssetsWithAttributes } from '../../services/assets';
    import { Search, Filter, ChevronRight, X } from 'lucide-react';
    import { AlarmNotification, FaultNotification } from './Notifications';
    
    function AssetList() {
      const { theme } = useTheme();
      const { currentSiteId } = useAuth();
      const [assets, setAssets] = useState([]);
      const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: 20,
        totalPages: 1,
        totalItems: 0
      });
      const [searchQuery, setSearchQuery] = useState('');
      const [locationLevels, setLocationLevels] = useState([]); // Array of arrays, each containing nodes for that level
      const [selectedLocations, setSelectedLocations] = useState([]); // Array of selected nodes, one per level
      const [loading, setLoading] = useState(false);
      const [enabledColumns, setEnabledColumns] = useState(['name', 'location', 'alarms']);
      const [availableColumns, setAvailableColumns] = useState(new Set());
    
      // Fetch initial location nodes (first level)
      useEffect(() => {
        const loadInitialLocations = async () => {
          try {
            const nodes = await fetchNodes(currentSiteId, null, true, ['Location']);
            setLocationLevels([nodes]);
            if (nodes.length > 0) {
              setSelectedLocations([nodes[0]]);
              // Get children from the node's children property
              if (nodes[0].children?.length > 0) {
                setLocationLevels(prev => [...prev, nodes[0].children]);
              }
            }
          } catch (error) {
            console.error('Error loading locations:', error);
          }
        };
        loadInitialLocations();
      }, [currentSiteId]);
    
      // Load assets based on filters
      useEffect(() => {
        const loadAssets = async () => {
          if(!selectedLocations.length) {
            return;
          }
          setLoading(true);
          try {
            const lastSelectedLocation = selectedLocations[selectedLocations.length - 1];
            const selectedLocationId = lastSelectedLocation?.id;
            
            const response = await fetchAssetsWithAttributes(
              currentSiteId,
              selectedLocationId,
              searchQuery,
              pagination.currentPage,
              pagination.pageSize
            );
            
            setAssets(response.items);
            setPagination(response.pagination);
    
            // Extract all possible info column names
            const infoColumns = new Set();
            response.items.forEach(asset => {
              asset.info.forEach(info => {
                infoColumns.add(info.name);
              });
            });
            setAvailableColumns(infoColumns);
    
          } catch (error) {
            console.error('Error loading assets:', error);
          }
          setLoading(false);
        };
    
        loadAssets();
      }, [currentSiteId, selectedLocations, searchQuery, pagination.currentPage, pagination.pageSize]);
    
      const handleLocationSelect = async (node, level) => {
        // If node is null (All selected), remove all levels after this one
        if (!node && level > 0) {
          setSelectedLocations(prev => prev.slice(0, level));
          setLocationLevels(prev => prev.slice(0, level + 1));
          setPagination(prev => ({ ...prev, currentPage: 1 }));
          return;
        }
    
        // Update selected locations up to this level
        setSelectedLocations(prev => {
          const newSelected = [...prev.slice(0, level)];
          if (node) {
            newSelected[level] = node;
          }
          return newSelected;
        });
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    
        // If a node is selected and it's not the first level, fetch its children with their children
        if (node && level > 0) {
          try {
            const nodes = await fetchNodes(currentSiteId, node.id, true, ['Location']);
            setLocationLevels(prev => {
              const newLevels = [...prev.slice(0, level + 1)];
              newLevels[level + 1] = nodes;
              return newLevels;
            });
          } catch (error) {
            console.error('Error loading child locations:', error);
          }
        } else if (node) {
          // For first level, use children from the node's children property
          setLocationLevels(prev => {
            const newLevels = [...prev.slice(0, level + 1)];
            if (node.children?.length > 0) {
              newLevels[level + 1] = node.children;
            }
            return newLevels;
          });
        }
      };
      
      // Helper function to generate page numbers with ellipsis
      const getPageNumbers = () => {
        const pages = [];
        const total = pagination.totalPages;
        const current = pagination.currentPage;
        const maxPagesToShow = 4;
        const half = Math.floor(maxPagesToShow / 2);
    
        let start = Math.max(1, current - half);
        let end = Math.min(total, current + half);
    
        if (current - 1 <= half) {
          end = Math.min(total, maxPagesToShow);
        }
        if (total - current <= half) {
          start = Math.max(1, total - maxPagesToShow + 1);
        }
    
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
    
        if (start > 2) {
          pages.unshift('...');
          pages.unshift(1);
        } else if (start === 2) {
          pages.unshift(1);
        }
    
        if (end < total - 1) {
          pages.push('...');
          pages.push(total);
        } else if (end === total - 1) {
          pages.push(total);
        }
    
        return pages;
      };
    
      return (
        <div className="flex flex-col h-[calc(100vh-64px)] p-2 md:p-4 space-y-2 md:space-y-4 w-[calc(100vw-16px)]">
          {/* Search and Filter Bar */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 w-full">
            <div className={`w-full md:flex-1 flex items-center px-2 md:px-3 py-2 rounded-lg border min-w-[200px] ${
              theme === 'light' ? 'border-gray-300' : 'border-gray-700'
            }`}>
              <Search className="w-4 md:w-5 h-4 md:h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search assets..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPagination(prev => ({ ...prev, currentPage: 1 })); }}
                className={`ml-2 outline-none bg-transparent w-full text-sm md:text-base ${
                  theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                }`}
              />
              {searchQuery && (
                <button 
                  onClick={() => { setSearchQuery(''); setPagination(prev => ({ ...prev, currentPage: 1 })); }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-4 md:w-5 h-4 md:h-5" />
                </button>
              )}
            </div>
    
            {/* Location Filters */}
            <div className="w-full md:w-auto flex flex-wrap items-center gap-2">
              {locationLevels.map((nodes, level) => {
                if (level === 0 || (locationLevels[level] && locationLevels[level].length > 0)) {
                  return (
                    <React.Fragment key={level}>
                      <select
                        value={selectedLocations[level]?.id || ''}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selected = selectedId ? nodes.find(n => n.id === parseInt(selectedId)) : null;
                          handleLocationSelect(selected, level);
                        }}
                        className={`flex-1 md:flex-none text-sm px-2 md:px-3 py-1 md:py-2 rounded-lg border ${
                          theme === 'light' 
                            ? 'border-gray-300 bg-white text-gray-900' 
                            : 'border-gray-700 bg-gray-800 text-gray-100'
                        }`}
                      >
                        {level === 0 ? (
                          nodes.map(node => (
                            <option key={node.id} value={node.id}>{node.name}</option>
                          ))
                        ) : (
                          <>
                            <option value="">All</option>
                            {nodes.map(node => (
                              <option key={node.id} value={node.id}>{node.name}</option>
                            ))}
                          </>
                        )}
                      </select>
                      {level < locationLevels.length - 1 && 
                       locationLevels[level + 1]?.length > 0 && 
                       selectedLocations[level] && (
                        <ChevronRight className="hidden md:block w-4 h-4 text-gray-500" />
                      )}
                    </React.Fragment>
                  );
                }
                return null;
              })}
            </div>
          </div>
    
          {/* Column Toggle Buttons */}
          {availableColumns.length > 0 && (
            <div className="flex flex-wrap gap-1 md:gap-2">
              {Array.from(availableColumns).map(column => (
                <button
                  key={column}
                  onClick={() => setEnabledColumns(prev => 
                    prev.includes(column) 
                      ? prev.filter(c => c !== column)
                      : [...prev, column]
                  )}
                  className={`px-2 md:px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center gap-1 whitespace-nowrap ${
                    enabledColumns.includes(column)
                      ? 'border-purple-500/30 text-purple-500 bg-purple-500/10'
                      : 'border-gray-500/30 text-gray-500 hover:bg-gray-500/10'
                  }`}
                >
                  {column}
                </button>
              ))}
            </div>
          )}
    
          {/* Assets Table */}
          <div className={`max-h-[calc(100vh-100px)] flex-1 overflow-auto border rounded-lg ${
            theme === 'light' ? 'border-gray-200' : 'border-gray-700'
          }`}>
            <div className="min-w-full overflow-x-auto">
              <div className="max-h-[calc(100vh-200px)] overflow-y-auto">
                <table className="w-full">
                  <thead className={`sticky top-0 z-10 ${
                    theme === 'light' ? 'bg-gray-50' : 'bg-gray-800'
                  }`}>
                    <tr>
                      <th className="px-2 md:px-4 py-2 text-left whitespace-nowrap text-xs md:text-sm">Name</th>
                      <th className="px-2 md:px-4 py-2 text-left whitespace-nowrap text-xs md:text-sm">Location</th>
                      <th className="px-2 md:px-4 py-2 text-left whitespace-nowrap text-xs md:text-sm">Alarms</th>
                      {enabledColumns
                        .filter(col => !['name', 'location', 'alarms'].includes(col))
                        .map(col => (
                          <th key={col} className="px-2 md:px-4 py-2 text-left whitespace-nowrap text-xs md:text-sm">{col}</th>
                        ))
                      }
                    </tr>
                  </thead>
                  <tbody>
                    {assets.map(asset => (
                      <tr key={asset.id} className={`border-t ${
                        theme === 'light' ? 'border-gray-200' : 'border-gray-700'
                      }`}>
                        <td className="px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm">{asset.name}</td>
                        <td className="px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm">{asset.location_name}</td>
                        <td className="px-2 md:px-4 py-2 whitespace-nowrap">
                          <div className="flex space-x-1 md:space-x-2">
                            <AlarmNotification count={asset.alarms.alarms} />
                            <FaultNotification count={asset.alarms.faults} />
                          </div>
                        </td>
                        {enabledColumns
                          .filter(col => !['name', 'location', 'alarms'].includes(col))
                          .map(col => (
                            <td key={col} className="px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm">
                              {asset.info.find(i => i.name === col)?.value || '-'}
                            </td>
                          ))
                        }
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
    
          {/* Pagination */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 md:gap-0">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 w-full md:w-auto">
              <span className={`text-xs md:text-sm ${
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              }`}>
                Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to {
                  Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)
                } of {pagination.totalItems} entries
              </span>
              
              <select
                value={pagination.pageSize}
                onChange={(e) => setPagination(prev => ({ ...prev, pageSize: Number(e.target.value), currentPage: 1 }))}
                className={`w-full md:w-auto px-2 py-1 rounded border text-xs md:text-sm ${
                  theme === 'light' 
                    ? 'border-gray-300 bg-white text-gray-900' 
                    : 'border-gray-700 bg-gray-800 text-gray-100'
                }`}
              >
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>
            
            <div className="flex flex-wrap gap-1 md:gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
                disabled={pagination.currentPage === 1}
                className={`px-2 md:px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center gap-1 whitespace-nowrap ${
                  pagination.currentPage === 1
                    ? 'border-gray-500/30 text-gray-500 cursor-not-allowed'
                    : 'border-gray-500/30 text-gray-500 hover:bg-gray-500/10'
                }`}
              >
                Previous
              </button>
              
              {getPageNumbers().map((page, index) => (
                typeof page === 'number' ? (
                  <button
                    key={`page-${page}`}
                    onClick={() => setPagination(prev => ({ ...prev, currentPage: page }))}
                    className={`px-2 md:px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center gap-1 whitespace-nowrap ${
                      pagination.currentPage === page
                        ? 'border-purple-500/30 text-purple-500 bg-purple-500/10'
                        : 'border-gray-500/30 text-gray-500 hover:bg-gray-500/10'
                    }`}
                  >
                    {page}
                  </button>
                ) : (
                  <span key={`ellipsis-${index}`} className="px-2 md:px-2.5 py-1 text-xs font-medium text-gray-500">
                    ...
                  </span>
                )
              ))}
              
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
                disabled={pagination.currentPage === pagination.totalPages}
                className={`px-2 md:px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center gap-1 whitespace-nowrap ${
                  pagination.currentPage === pagination.totalPages
                    ? 'border-gray-500/30 text-gray-500 cursor-not-allowed'
                    : 'border-gray-500/30 text-gray-500 hover:bg-gray-500/10'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    export default AssetList;
